import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from './utils';

export type IconButtonSize = 'sm' | 'md' | 'lg';
export type IconButtonVariant = 'ghost' | 'solid' | 'outline';

export type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  size?: IconButtonSize;
  variant?: IconButtonVariant;
  /** Required for accessibility — describes the button's action. */
  'aria-label': string;
  children: ReactNode;
};

const SIZE_CLS: Record<IconButtonSize, string> = {
  sm: 'w-7 h-7 rounded-xl',
  md: 'w-9 h-9 rounded-xl',
  lg: 'w-10 h-10 rounded-xl',
};

const VARIANT_CLS: Record<IconButtonVariant, string> = {
  ghost: 'text-ink-600 hover:text-ink-900 hover:bg-white/45',
  solid: 'bg-ink-900 hover:bg-ink-800 text-white',
  outline: 'border border-ink-200 hover:border-ink-400 text-ink-700 hover:text-ink-900 bg-white',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton({ size = 'md', variant = 'ghost', className, children, type = 'button', ...rest }, ref) {
    return (
      <button
        ref={ref}
        type={type}
        {...rest}
        className={cn(
          'btn-press focus-ring grid place-items-center cursor-pointer transition-colors',
          SIZE_CLS[size],
          VARIANT_CLS[variant],
          className,
        )}
      >
        {children}
      </button>
    );
  },
);
